/*
 * Purpose: AutoBlocks composer — Mission running vs Flow at rest
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAutoBlocksStore } from '@/stores/autoBlocksStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  Blocks,
  Plug,
  Globe,
  FlaskConical,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowRight,
  RotateCcw,
  ChevronDown,
  Map,
} from 'lucide-react';
import { GmailConnectorPanel } from '@/components/GmailConnectorPanel';
import { LinkedInConnectorPanel } from '@/components/LinkedInConnectorPanel';
import { ConnectorLibraryPanel } from '@/components/ConnectorLibraryPanel';
import { PlanReadinessPanel } from '@/components/PlanReadinessPanel';
import { DirectorTrail } from '@/components/intent/DirectorTrail';
import { SuperIntentInterpreter } from '@/components/intent/SuperIntentInterpreter';
import { IntentConfirmPanel } from '@/components/intent/IntentConfirmPanel';
import { MissionTrail } from '@/components/intent/MissionTrail';
import { ArtifactStateBadge } from '@/components/intent/ArtifactStateBadge';
import { attachWorkflowPreview } from '@/services/autoblocks/workflowAssembler';
import { rehydratePlanConnectors } from '@/services/autoblocks/planReadiness';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { getBlockTypeLabel } from '@/lib/blockTypes';

function StatusIcon({ status }: { status: string }) {
  if (status === 'pass') return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
  if (status === 'simulated') return <FlaskConical className="h-4 w-4 text-blue-500 shrink-0" />;
  if (status === 'warn') return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
  return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
}

export function AutoBlocksComposer() {
  const navigate = useNavigate();
  const location = useLocation();
  const autoInterpretRan = useRef(false);
  const {
    phase,
    artifactState,
    composeMode,
    missionTrail,
    outcomeText,
    interpretation,
    plan,
    director,
    dryRunReport,
    isInterpreting,
    isDirectorWorking,
    isPlanning,
    isDryRunning,
    error,
    setOutcomeText,
    interpretOutcome,
    confirmAndPlan,
    expressLaneProceed,
    runDryRunTest,
    approveAndOpenInBuilder,
    reset,
  } = useAutoBlocksStore();

  const proposedRef = useRef<HTMLElement>(null);
  const dryRunRef = useRef<HTMLElement>(null);
  const [describeOpen, setDescribeOpen] = useState(true);

  const confidencePct = plan ? Math.round(plan.confidence * 100) : 0;
  const isMission = artifactState === 'mission';
  const isWorking = isInterpreting || isPlanning || isDirectorWorking || phase === 'express_running';

  const showLinkedInPanel =
    plan &&
    (plan.matchedTemplateId === 'linkedin_job_hunt_pipeline' ||
      plan.connectors.some((c) => c.id.includes('linkedin')) ||
      /\blinkedin\b/i.test(plan.rawOutcome));

  useEffect(() => {
    const state = location.state as { autoInterpret?: boolean; fromHome?: boolean } | null;
    if (!state?.autoInterpret || autoInterpretRan.current || !outcomeText.trim()) return;
    autoInterpretRan.current = true;
    void interpretOutcome();
  }, [location.state, outcomeText, interpretOutcome]);

  useEffect(() => {
    if (!isPlanning || plan) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isPlanning, plan]);

  useEffect(() => {
    if (!plan || isPlanning) return;
    setDescribeOpen(false);
    const timer = window.setTimeout(() => {
      proposedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [plan, isPlanning]);

  useEffect(() => {
    if (!dryRunReport || isDryRunning) return;
    const timer = window.setTimeout(() => {
      dryRunRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [dryRunReport, isDryRunning]);

  const handleReset = () => {
    reset();
    setDescribeOpen(true);
  };

  const refreshPlanReadiness = (updated: NonNullable<typeof plan>) => {
    useAutoBlocksStore.setState({ plan: rehydratePlanConnectors(updated) });
  };

  const describeCard = (
    <Collapsible open={describeOpen} onOpenChange={setDescribeOpen}>
      <Card className={cn((plan || phase === 'confirm') && !describeOpen && 'border-dashed')}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <ArtifactStateBadge artifactState={artifactState} composeMode={composeMode} />
            {(plan || interpretation) && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8" onClick={handleReset}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  Start over
                </Button>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 gap-1">
                    {describeOpen ? (
                      <>
                        Collapse
                        <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                      </>
                    ) : (
                      <>
                        Edit outcome
                        <ChevronDown className="h-4 w-4 transition-transform" />
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
            )}
          </div>
          {!describeOpen && outcomeText && (
            <CardDescription className="line-clamp-2 mt-2">{outcomeText}</CardDescription>
          )}
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <SuperIntentInterpreter
              variant="compose"
              value={outcomeText}
              onChange={setOutcomeText}
              onSubmit={() => void interpretOutcome()}
              disabled={isWorking}
              isLoading={isInterpreting}
              error={error}
              showExamples={!plan}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );

  const confirmCard =
    phase === 'confirm' && interpretation ? (
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <IntentConfirmPanel
          interpretation={interpretation}
          composeMode={composeMode}
          isWorking={isWorking}
          onConfirmGuided={() => void confirmAndPlan()}
          onExpressProceed={() => void expressLaneProceed()}
          onReinterpret={() => void interpretOutcome()}
        />
      </motion.section>
    ) : null;

  const planCard = plan ? (
    <motion.section
      ref={proposedRef}
      id="autoblocks-flow"
      className="scroll-mt-20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <Card className="ring-1 ring-primary/20 shadow-md">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Blocks className="h-5 w-5 ui-icon" />
              <CardTitle className="text-lg">
                {isMission ? PRODUCT_TERMS.mission : PRODUCT_TERMS.autoBlockFlow}
              </CardTitle>
            </div>
            <Badge variant="secondary">{confidencePct}% match confidence</Badge>
            {plan.plannerSource && (
              <Badge variant={plan.plannerSource === 'llm' ? 'default' : 'outline'}>
                {plan.plannerSource === 'llm' ? 'AI planner' : 'Rule planner'}
              </Badge>
            )}
            {plan.director?.pacing === 'journey_tube' && (
              <Badge variant="outline" className="border-primary/40">
                Journey Tube
              </Badge>
            )}
          </div>
          <CardDescription>
            {plan.steps.length || plan.workflowPreview?.nodes.length || 0}{' '}
            {(plan.steps.length || plan.workflowPreview?.nodes.length || 0) === 1
              ? PRODUCT_TERMS.block.toLowerCase()
              : PRODUCT_TERMS.blocks.toLowerCase()}{' '}
            — {plan.interpretedGoal}
          </CardDescription>
          <Progress value={confidencePct} className="h-1.5 mt-2" />
        </CardHeader>
        <CardContent className="space-y-6">
          <PlanReadinessPanel plan={plan} onPlanUpdate={refreshPlanReadiness} />

          {showLinkedInPanel && (
            <LinkedInConnectorPanel onConnected={() => refreshPlanReadiness(plan)} />
          )}

          {(plan.gmailInboxId || plan.useGmailInbound || plan.steps.some((s) => s.subtype === 'gmail_inbound')) && (
            <GmailConnectorPanel
              plan={plan}
              onInboxReady={(inboxId, postUrl) => {
                const next = attachWorkflowPreview({
                  ...plan,
                  gmailInboxId: inboxId,
                  gmailPostUrl: postUrl,
                  useGmailInbound: true,
                });
                refreshPlanReadiness(next);
              }}
            />
          )}

          {plan.resolvedConnectors && plan.resolvedConnectors.length > 0 && (
            <ConnectorLibraryPanel plan={plan} />
          )}

          <section>
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
              <Blocks className="h-4 w-4 ui-icon" />
              {PRODUCT_TERMS.mission} map preview
            </h3>
            <ScrollArea className="h-[min(240px,40vh)] rounded-md border">
              <ol className="p-3 space-y-2">
                {(plan.steps.length > 0 ? plan.steps : []).map((step, i) => (
                  <li key={step.id} className="flex gap-3 text-sm p-2 rounded-md bg-muted/40">
                    <span className="font-mono text-muted-foreground w-6 shrink-0">{i + 1}.</span>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{step.label}</p>
                      <p className="text-xs text-muted-foreground">{step.rationale}</p>
                      <Badge variant="outline" className="mt-1 text-[10px]">
                        {getBlockTypeLabel(step.type, step.subtype)}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ol>
            </ScrollArea>
          </section>

          {plan.connectors.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Plug className="h-4 w-4" />
                Connectors needed
              </h3>
              <div className="space-y-2">
                {plan.connectors.map((c) => (
                  <div key={c.id} className="rounded-md border p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-medium">{c.name}</span>
                      <Badge
                        variant={c.status === 'needs_credentials' ? 'destructive' : 'secondary'}
                        className="text-[10px]"
                      >
                        {c.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-xs mb-2">{c.purpose}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {plan.apis.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                APIs identified
              </h3>
              <ul className="space-y-2">
                {plan.apis.map((a) => (
                  <li key={a.id} className="text-sm rounded-md border p-2">
                    <span className="font-medium">{a.name}</span>
                    <span className="text-muted-foreground text-xs block">{a.purpose}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(plan.assumptions.length > 0 || plan.gaps.length > 0) && (
            <section className="grid sm:grid-cols-2 gap-3">
              {plan.assumptions.map((a) => (
                <p key={a} className="text-xs text-muted-foreground flex gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {a}
                </p>
              ))}
              {plan.gaps.map((g) => (
                <p key={g} className="text-xs text-amber-600 dark:text-amber-400 flex gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {g}
                </p>
              ))}
            </section>
          )}

          <Separator />

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => void runDryRunTest()}
              disabled={isDryRunning}
              className="w-full sm:w-auto"
            >
              <FlaskConical className="h-4 w-4 mr-2" />
              {isDryRunning ? 'Dry running…' : 'Dry run test'}
            </Button>
            <Button
              onClick={() => {
                approveAndOpenInBuilder();
                navigate('/builder');
              }}
              disabled={!dryRunReport?.readyForReview}
              className="w-full sm:w-auto"
            >
              Approve & save {PRODUCT_TERMS.autoBlockFlow}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          {!dryRunReport && (
            <p className="text-xs text-muted-foreground">
              Dry run first — then approve to crystallize your {PRODUCT_TERMS.autoBlockFlow} at rest.
            </p>
          )}
        </CardContent>
      </Card>
    </motion.section>
  ) : null;

  const dryRunCard = dryRunReport ? (
    <motion.section
      ref={dryRunRef}
      id="autoblocks-dry-run"
      className="scroll-mt-20"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <Card
        className={cn(
          'border-2',
          dryRunReport.passed ? 'border-green-500/40' : 'border-destructive/40'
        )}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            <CardTitle className="text-lg">Dry run report</CardTitle>
            <Badge variant={dryRunReport.passed ? 'default' : 'destructive'}>
              {dryRunReport.passed ? 'Ready for review' : 'Issues found'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <ScrollArea className="h-[min(280px,45vh)] rounded-md border">
            <ul className="divide-y">
              {dryRunReport.stepResults
                .filter((s) => s.status !== 'warn' || s.message)
                .slice(0, 24)
                .map((step) => (
                  <li key={step.nodeId} className="p-3 text-sm flex gap-3">
                    <StatusIcon status={step.status} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{step.label}</p>
                      {step.message && (
                        <p className="text-xs text-muted-foreground">{step.message}</p>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </ScrollArea>
          {dryRunReport.readyForReview && (
            <Button
              className="w-full sm:w-auto"
              onClick={() => {
                approveAndOpenInBuilder();
                navigate('/builder');
              }}
            >
              Approve & open Flow Map
              <Map className="h-4 w-4 ml-2" />
            </Button>
          )}
        </CardContent>
      </Card>
    </motion.section>
  ) : null;

  const workingCard =
    isWorking && !plan ? (
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="border-dashed border-primary/30">
          <CardContent className="py-10 flex flex-col items-center gap-3 text-muted-foreground">
            <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <p className="text-sm font-medium">
              {phase === 'interpreting' && 'Interpreting your outcome…'}
              {phase === 'express_running' && 'Express lane — planning Mission…'}
              {(phase === 'director' || isDirectorWorking) && 'Director expanding your request…'}
              {phase === 'planning' && !isDirectorWorking && 'Planning your system…'}
            </p>
          </CardContent>
        </Card>
      </motion.section>
    ) : null;

  const flowReadyCard =
    phase === 'approved' && artifactState === 'flow' ? (
      <Card className="border-green-500/30 bg-green-500/5">
        <CardContent className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <p className="font-semibold flex items-center justify-center sm:justify-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Your {PRODUCT_TERMS.autoBlockFlow} is ready
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              At rest — all steps known and repeatable. Open Flow Map in Builder to customize.
            </p>
          </div>
          <Button onClick={() => navigate('/builder')}>
            Open Flow Map
            <Map className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    ) : null;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-4xl mx-auto w-full">
      {isMission && missionTrail.length > 0 && (
        <MissionTrail steps={missionTrail} composeMode={composeMode} />
      )}

      <DirectorTrail
        director={director ?? plan?.director}
        brandedIdeas={plan?.brandedIdeas}
        isWorking={isDirectorWorking || (isPlanning && phase === 'director')}
      />

      <AnimatePresence mode="popLayout">
        {workingCard}
        {confirmCard}
        {planCard}
        {dryRunCard}
        {flowReadyCard}
      </AnimatePresence>

      {(phase === 'describe' || phase === 'confirm' || plan) && describeCard}
    </div>
  );
}
